import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import { getMySchedule } from "api/user";
import { getMyTours } from "api/travel";
import NoData from "components/common/NoData";
import dayjs from "dayjs";
import { getKstDday } from "utils/util";

const getDday = getKstDday;

const formatDateRange = (startDate, endDate) => {
    const s = dayjs(startDate);
    const e = dayjs(endDate);
    const nights = e.diff(s, "day");
    return `${s.format("M/D")}~${e.format("M/D")} (${nights}박${nights + 1}일)`;
};

const ScheduleCard = ({ item }) => {
    const navigate = useNavigate();
    const isTour = item._type === "tour";
    const isPast = item._isPast;
    const d = dayjs(item._date);

    return (
        <div
            className={`reservation-card ${isPast ? "reservation-card--past" : ""}`}
            onClick={item._onClick}
        >
            <div className={`reservation-date-block ${isTour ? "date-block--tour" : ""} ${isPast ? "date-block--past" : ""}`}>
                <span className="reservation-date-month">{d.format("M")}월</span>
                <span className={`reservation-date-day ${isTour && !isPast ? "day--tour" : ""}`}>{d.format("D")}</span>
            </div>
            <div className="reservation-card-content">
                <div className="reservation-card-title-row">
                    <span className={`reservation-type-badge ${isTour ? "badge--tour" : "badge--lesson"}`}>
                        {isTour ? "투어" : "강습"}
                    </span>
                    <p className="reservation-card-title">{item._title}</p>
                </div>
                <p className="reservation-card-detail">{item._detail}</p>
            </div>
            <span className={`reservation-card-dday ${isPast ? "dday--past" : ""} ${isTour && !isPast ? "dday--tour" : ""}`}>
                {isPast ? "완료" : getDday(item._date)}
            </span>
        </div>
    );
};

const normalizeLesson = (info, navigate) => {
    const scheduleInfo = info.schedule_info || info;
    const date = info.date || scheduleInfo.date;
    const instructor = scheduleInfo.instructor_name || "";
    const location = scheduleInfo.location_name || "";
    const startTime = scheduleInfo.start_time?.slice(0, 5);
    const endTime = scheduleInfo.end_time?.slice(0, 5);
    const details = [instructor, location, startTime && endTime ? `${startTime}~${endTime}` : ""].filter(Boolean).join(" | ");

    return {
        _type: "lesson",
        _date: date,
        _title: scheduleInfo.schedule_title || scheduleInfo.lesson_product_display_name,
        _detail: details,
        _isPast: false,
        _onClick: () => {
            const type = info.schedule_info ? "class" : "practice";
            navigate(`/reservation/${type}/${info.schedule || info.practice_schedule}`);
        },
    };
};

const normalizeTour = (tour, navigate) => {
    const detail = [tour.location, formatDateRange(tour.start_date, tour.end_date)].filter(Boolean).join(" | ");

    return {
        _type: "tour",
        _date: tour.start_date,
        _title: tour.name,
        _detail: detail,
        _isPast: tour.status === "COMPLETED",
        _onClick: () => navigate(`/tour/detail/${tour.id}`),
    };
};

const ReservationTab = () => {
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const [upcomingItems, setUpcomingItems] = useState([]);
    const [pastItems, setPastItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (userInfo !== null) {
            setIsLoading(true);
            Promise.all([
                getMySchedule().catch(() => ({ lessons: [], practices: [] })),
                getMyTours(true).catch(() => []),
            ])
                .then(([scheduleRes, tours]) => {
                    const lessonItems = [...scheduleRes.lessons, ...scheduleRes.practices].map((item) =>
                        normalizeLesson(item, navigate)
                    );
                    const tourItems = tours.map((tour) => normalizeTour(tour, navigate));
                    const all = [...lessonItems, ...tourItems];

                    const today = dayjs().startOf("day");

                    // 강습의 past 판정
                    all.forEach((item) => {
                        if (item._type === "lesson") {
                            item._isPast = dayjs(item._date).isBefore(today);
                        }
                    });

                    const upcoming = all
                        .filter((item) => !item._isPast)
                        .sort((a, b) => new Date(a._date) - new Date(b._date));
                    const past = all
                        .filter((item) => item._isPast)
                        .sort((a, b) => new Date(b._date) - new Date(a._date));

                    setUpcomingItems(upcoming);
                    setPastItems(past);
                })
                .finally(() => setIsLoading(false));
        }
    }, [userInfo, navigate]);

    if (isLoading) {
        return (
            <div className="reservation-tab" style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                불러오는 중...
            </div>
        );
    }

    if (upcomingItems.length === 0 && pastItems.length === 0) {
        return (
            <div className="reservation-tab">
                <NoData title="🗓️" desc="아직 예약한 일정이 없어요." />
            </div>
        );
    }

    return (
        <div className="reservation-tab">
            {upcomingItems.length > 0 && (
                <div className="reservation-section">
                    <h3 className="reservation-section-title">📅 다가오는 예약</h3>
                    <div className="reservation-list">
                        {upcomingItems.map((item, idx) => (
                            <ScheduleCard item={item} key={`upcoming-${idx}`} />
                        ))}
                    </div>
                </div>
            )}
            {pastItems.length > 0 && (
                <div className="reservation-section">
                    <h3 className="reservation-section-title">📋 지난 예약</h3>
                    <div className="reservation-list">
                        {pastItems.map((item, idx) => (
                            <ScheduleCard item={item} key={`past-${idx}`} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservationTab;
